import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Users, Clock, CheckCircle, XCircle, Search, ArrowRight, Calendar, Target, BookOpen, GraduationCap, Mail, Plus, X, Copy, Crown, User, Trash2, ChevronDown, ArrowUpCircle, ArrowDownCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const ClientList = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('status') || 'all');
  const [searchTerm, setSearchTerm] = useState('');

  // Client bulk action state
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [bulkActioning, setBulkActioning] = useState(false);
  const [showCoachPicker, setShowCoachPicker] = useState(false);

  // Invitation state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', subscription_tier: 'free', message: '' });
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchInvitations();
    fetchCoaches();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await API.get('/admin/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await API.get('/admin/invitations');
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await API.get('/admin/coaches');
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    }
  };

  const handleApprove = async (clientId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await API.post(`/admin/clients/${clientId}/approve`);
      toast.success('Client approved!');
      fetchClients();
    } catch (error) {
      toast.error('Failed to approve client');
    }
  };

  const handleReject = async (clientId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to reject this client?')) return;
    try {
      await API.post(`/admin/clients/${clientId}/reject`);
      toast.success('Client rejected');
      fetchClients();
    } catch (error) {
      toast.error('Failed to reject client');
    }
  };

  // ===== CLIENT BULK ACTIONS =====
  const toggleClientSelect = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedClients(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAllClients = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const handleBulkAction = async (action, extra = {}) => {
    if (!selectedClients.length) return;
    const count = selectedClients.length;

    if (action === 'delete' && !window.confirm(`Delete ${count} client(s) and ALL their data? This cannot be undone.`)) return;

    setBulkActioning(true);
    try {
      const res = await API.post('/admin/clients/bulk-action', { ids: selectedClients, action, ...extra });
      toast.success(res.data.message);
      setSelectedClients([]);
      setShowBulkAction(false);
      setShowCoachPicker(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setBulkActioning(false);
    }
  };

  // ===== INVITATION HANDLERS =====
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await API.post('/admin/invitations', inviteForm);
      setCreatedInvite({ ...response.data, full_name: inviteForm.full_name, email: inviteForm.email });
      toast.success('Invitation created!');
      fetchInvitations();
      setInviteForm({ email: '', full_name: '', subscription_tier: 'free', message: '' });
    } catch (error) {
      toast.error('Failed to create invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInvite = async (invitationId) => {
    if (!window.confirm('Delete this invitation?')) return;
    try {
      await API.delete(`/admin/invitations/${invitationId}`);
      toast.success('Invitation deleted');
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleBulkDeleteInvites = async () => {
    if (!selectedInvites.length) return;
    if (!window.confirm(`Delete ${selectedInvites.length} invitation(s)?`)) return;
    setBulkDeleting(true);
    try {
      await API.post('/admin/invitations/bulk-delete', { ids: selectedInvites });
      toast.success(`${selectedInvites.length} invitation(s) deleted`);
      setSelectedInvites([]);
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to delete invitations');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleInviteSelect = (id) => {
    setSelectedInvites(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAllInvites = () => {
    const pending = invitations.filter(i => !i.used);
    if (selectedInvites.length === pending.length) {
      setSelectedInvites([]);
    } else {
      setSelectedInvites(pending.map(i => i.id));
    }
  };

  const openEmailClient = (invite) => {
    const registerUrl = `${window.location.origin}/register`;
    const subNote = invite.subscription_tier === 'pro_yearly'
      ? '\n\n Your account includes a complimentary Pro Yearly subscription with full access for 1 year!' : '';
    const personalMsg = invite.message ? `\n\n${invite.message}` : '';
    const subject = encodeURIComponent('Your Quiet Mind Golf Invitation');
    const body = encodeURIComponent(
`Hi ${invite.full_name || 'there'},

I'd like to invite you to join Quiet Mind Golf's client portal where you can track your sessions, view your progress, and communicate with me directly.${subNote}${personalMsg}

To get started:
1. Go to: ${registerUrl}
2. Enter your invitation code: ${invite.code}
3. Create your account

I look forward to working with you!

Best regards,
Scott Ritchie
Quiet Mind Golf`
    );
    window.location.href = `mailto:${invite.email}?subject=${subject}&body=${body}`;
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const closeModal = () => {
    setShowInviteForm(false);
    setCreatedInvite(null);
    setInviteForm({ email: '', full_name: '', subscription_tier: 'free', message: '' });
  };

  const filteredClients = clients.filter(client => {
    const matchesFilter = filter === 'all' || client.status === filter;
    const matchesSearch = !searchTerm ||
      client.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="flex items-center gap-1 px-2 py-1 bg-[#BC562B]/10 text-[#BC562B] text-xs font-semibold"><Clock className="w-3 h-3" />Pending</span>,
      approved: <span className="flex items-center gap-1 px-2 py-1 bg-[#2C5530]/10 text-[#2C5530] text-xs font-semibold"><CheckCircle className="w-3 h-3" />Approved</span>,
      rejected: <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold"><XCircle className="w-3 h-3" />Rejected</span>,
    };
    return badges[status] || null;
  };

  const pendingInvites = invitations.filter(i => !i.used);

  return (
    <div data-testid="client-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Clients</h1>
          <p className="text-[#57534E]">Manage clients and invitations.</p>
        </div>
        <button
          onClick={() => { setTab('invitations'); setShowInviteForm(true); }}
          className="bg-[#BC562B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-all duration-300 flex items-center gap-2 self-start"
          data-testid="invite-client-btn"
        >
          <Plus className="w-4 h-4" />
          Invite Client
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E7E5E4]">
        <button
          onClick={() => setTab('clients')}
          className={`px-6 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            tab === 'clients' ? 'border-[#1A1A1A] text-[#1A1A1A]' : 'border-transparent text-[#737373] hover:text-[#1A1A1A]'
          }`}
          data-testid="tab-clients"
        >
          <span className="flex items-center gap-2"><Users className="w-4 h-4" />Clients ({clients.length})</span>
        </button>
        <button
          onClick={() => setTab('invitations')}
          className={`px-6 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            tab === 'invitations' ? 'border-[#1A1A1A] text-[#1A1A1A]' : 'border-transparent text-[#737373] hover:text-[#1A1A1A]'
          }`}
          data-testid="tab-invitations"
        >
          <span className="flex items-center gap-2"><Mail className="w-4 h-4" />Invitations ({invitations.length})</span>
        </button>
      </div>

      {/* ==================== CLIENTS TAB ==================== */}
      {tab === 'clients' && (
        <>
          {/* Bulk Action Bar */}
          {selectedClients.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 p-3 sm:p-4 bg-[#F2F0E9] border border-[#E7E5E4]" data-testid="bulk-action-bar">
              <span className="text-sm font-semibold text-[#1A1A1A]">{selectedClients.length} selected</span>
              <div className="w-px h-6 bg-[#E7E5E4] hidden sm:block"></div>

              <div className="relative">
                <button
                  onClick={() => setShowCoachPicker(!showCoachPicker)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#2C5530] text-white text-sm font-semibold hover:bg-[#1E3A21] transition-colors"
                  data-testid="bulk-assign-coach-btn"
                >
                  <UserPlus className="w-4 h-4" />Assign Coach<ChevronDown className="w-3 h-3" />
                </button>
                {showCoachPicker && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-[#E7E5E4] shadow-lg z-50 max-h-60 overflow-y-auto" data-testid="coach-picker-dropdown">
                    <button
                      onClick={() => handleBulkAction('assign-coach', { coach_id: null })}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F2F0E9] text-[#737373] border-b border-[#E7E5E4]"
                    >
                      Unassign Coach
                    </button>
                    {coaches.map(coach => (
                      <button
                        key={coach.id}
                        onClick={() => handleBulkAction('assign-coach', { coach_id: coach.id })}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F2F0E9] text-[#1A1A1A]"
                        data-testid={`assign-coach-${coach.id}`}
                      >
                        <span className="font-medium">{coach.full_name}</span>
                        <span className="text-[#737373] ml-2">({coach.total_clients} students)</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => handleBulkAction('upgrade')}
                disabled={bulkActioning}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#BC562B] text-white text-sm font-semibold hover:bg-[#9A4520] transition-colors disabled:opacity-50"
                data-testid="bulk-upgrade-btn"
              >
                <ArrowUpCircle className="w-4 h-4" />Upgrade to Pro
              </button>

              <button
                onClick={() => handleBulkAction('downgrade')}
                disabled={bulkActioning}
                className="flex items-center gap-1.5 px-4 py-2 border border-[#E7E5E4] bg-white text-[#57534E] text-sm font-semibold hover:bg-[#F2F0E9] transition-colors disabled:opacity-50"
                data-testid="bulk-downgrade-btn"
              >
                <ArrowDownCircle className="w-4 h-4" />Downgrade to Free
              </button>

              <button
                onClick={() => handleBulkAction('delete')}
                disabled={bulkActioning}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                data-testid="bulk-delete-clients-btn"
              >
                <Trash2 className="w-4 h-4" />Delete
              </button>

              <button onClick={() => { setSelectedClients([]); setShowCoachPicker(false); }} className="ml-auto text-sm text-[#737373] hover:text-[#1A1A1A]">Clear</button>
            </div>
          )}

          {/* Filters & Search */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#737373] flex-shrink-0">
                <input
                  type="checkbox"
                  checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                  onChange={toggleSelectAllClients}
                  className="w-4 h-4 accent-[#2C5530]"
                  data-testid="select-all-clients"
                />
                All
              </label>
              {['all', 'pending', 'approved', 'rejected'].map(value => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors flex-shrink-0 ${
                    filter === value ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#1A1A1A]'
                  }`}
                  data-testid={`filter-${value}`}
                >
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </button>
              ))}
            </div>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E7E5E4]"
                data-testid="search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#E7E5E4]">
              <Users className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
              <p className="text-[#737373]">No clients found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredClients.map((client, index) => (
                <div
                  key={client.id}
                  className={`bg-white border p-4 sm:p-6 transition-all ${selectedClients.includes(client.id) ? 'border-[#2C5530] bg-[#2C5530]/[0.02]' : 'border-[#E7E5E4] hover:border-[#BC562B]/30 hover:shadow-md'}`}
                  data-testid={`client-card-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={(e) => toggleClientSelect(client.id, e)}
                      className="mt-1.5 w-4 h-4 accent-[#2C5530] flex-shrink-0 cursor-pointer"
                      data-testid={`select-client-${index}`}
                    />
                    <Link to={`/admin/clients/${client.id}`} className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-['Playfair_Display'] text-lg sm:text-xl text-[#1A1A1A]">{client.full_name}</h3>
                            {getStatusBadge(client.status)}
                            {client.subscription_tier === 'pro' ? (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#BC562B] text-white text-xs font-semibold"><Crown className="w-3 h-3" />Pro</span>
                            ) : (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-[#E7E5E4] text-[#57534E] text-xs font-semibold">Free</span>
                            )}
                          </div>
                          <p className="text-[#57534E] mb-3 truncate text-sm">{client.email}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-[#737373]">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Joined {formatDate(client.created_at)}</span>
                            {client.coach_name && (
                              <span className="flex items-center gap-1 text-[#2C5530]"><GraduationCap className="w-3.5 h-3.5" />{client.coach_name}</span>
                            )}
                          </div>
                          <div className="flex gap-4 sm:gap-6 mt-3 pt-3 border-t border-[#E7E5E4]">
                            <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#2C5530]" /><span className="text-xs sm:text-sm"><strong>{client.total_sessions}</strong> sessions</span></div>
                            <div className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-[#BC562B]" /><span className="text-xs sm:text-sm"><strong>{client.total_games}</strong> games</span></div>
                            <div className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-[#2C5530]" /><span className="text-xs sm:text-sm"><strong>{client.total_diary_entries}</strong> diary</span></div>
                          </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                          {client.status === 'pending' && (
                            <>
                              <button onClick={(e) => handleApprove(client.id, e)} className="flex items-center gap-1 px-4 py-2 bg-[#2C5530] text-white text-sm font-semibold hover:bg-[#1E3A21] transition-colors" data-testid={`approve-btn-${index}`}><CheckCircle className="w-4 h-4" />Approve</button>
                              <button onClick={(e) => handleReject(client.id, e)} className="flex items-center gap-1 px-4 py-2 border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors" data-testid={`reject-btn-${index}`}><XCircle className="w-4 h-4" />Reject</button>
                            </>
                          )}
                          <ArrowRight className="w-5 h-5 text-[#737373]" />
                        </div>
                      </div>
                      {/* Mobile approve/reject */}
                      {client.status === 'pending' && (
                        <div className="flex sm:hidden items-center gap-2 mt-3 pt-3 border-t border-[#E7E5E4]">
                          <button onClick={(e) => handleApprove(client.id, e)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#2C5530] text-white text-sm font-semibold hover:bg-[#1E3A21] transition-colors" data-testid={`approve-btn-mobile-${index}`}><CheckCircle className="w-4 h-4" />Approve</button>
                          <button onClick={(e) => handleReject(client.id, e)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors" data-testid={`reject-btn-mobile-${index}`}><XCircle className="w-4 h-4" />Reject</button>
                        </div>
                      )}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== INVITATIONS TAB ==================== */}
      {tab === 'invitations' && (
        <>
          {/* Bulk Actions Bar */}
          {selectedInvites.length > 0 && (
            <div className="flex items-center gap-4 mb-4 p-3 bg-red-50 border border-red-200">
              <span className="text-sm font-medium text-red-700">{selectedInvites.length} selected</span>
              <button
                onClick={handleBulkDeleteInvites}
                disabled={bulkDeleting}
                className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                data-testid="bulk-delete-invitations-btn"
              >
                <Trash2 className="w-4 h-4" />
                {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
              </button>
              <button onClick={() => setSelectedInvites([])} className="text-sm text-[#737373] hover:text-[#1A1A1A]">Clear</button>
            </div>
          )}

          {/* Invite Button */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div className="flex items-center gap-3">
              {pendingInvites.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#737373]">
                  <input
                    type="checkbox"
                    checked={selectedInvites.length === pendingInvites.length && pendingInvites.length > 0}
                    onChange={toggleSelectAllInvites}
                    className="w-4 h-4 accent-[#BC562B]"
                    data-testid="select-all-invitations"
                  />
                  Select all pending
                </label>
              )}
            </div>
            <button
              onClick={() => setShowInviteForm(true)}
              className="bg-[#BC562B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#9A4520] transition-colors flex items-center gap-2 flex-shrink-0"
              data-testid="new-invitation-btn"
            >
              <Plus className="w-4 h-4" />
              New Invitation
            </button>
          </div>

          {/* Invitations List */}
          {invitations.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#E7E5E4]">
              <Mail className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
              <p className="text-[#737373] mb-4">No invitations created yet</p>
              <button onClick={() => setShowInviteForm(true)} className="text-[#BC562B] font-semibold hover:underline">Create your first invitation</button>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((inv, index) => (
                <div key={inv.id} className="bg-white border border-[#E7E5E4] p-4 sm:p-5" data-testid={`invitation-card-${index}`}>
                  <div className="flex items-start gap-3">
                    {!inv.used && (
                      <input
                        type="checkbox"
                        checked={selectedInvites.includes(inv.id)}
                        onChange={() => toggleInviteSelect(inv.id)}
                        className="mt-1 w-4 h-4 accent-[#BC562B] flex-shrink-0"
                        data-testid={`select-invite-${index}`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-semibold text-[#1A1A1A] truncate">{inv.full_name || inv.email}</p>
                        {inv.used ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#2C5530]/10 text-[#2C5530] text-xs font-semibold"><CheckCircle className="w-3 h-3" />Used</span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#BC562B]/10 text-[#BC562B] text-xs font-semibold"><Clock className="w-3 h-3" />Pending</span>
                        )}
                        {inv.subscription_tier === 'pro_yearly' ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#BC562B] text-white text-xs font-semibold"><Crown className="w-3 h-3" />Pro</span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#E7E5E4] text-[#57534E] text-xs font-semibold"><User className="w-3 h-3" />Free</span>
                        )}
                      </div>
                      <p className="text-sm text-[#57534E] truncate">{inv.email}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#737373] mt-1">
                        <span>Created: {formatDate(inv.created_at)}</span>
                        <span>Expires: {formatDate(inv.expires_at)}</span>
                      </div>
                      {!inv.used && (
                        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#F2F0E9]">
                          <div className="bg-[#F2F0E9] px-3 py-1.5">
                            <p className="text-[10px] text-[#737373]">Code</p>
                            <p className="font-mono font-semibold text-xs sm:text-sm text-[#1A1A1A]">{inv.code}</p>
                          </div>
                          <button onClick={() => copyInviteCode(inv.code)} className="p-2 hover:bg-[#F2F0E9] transition-colors" title="Copy code"><Copy className="w-4 h-4 text-[#737373]" /></button>
                          <button onClick={() => openEmailClient(inv)} className="p-2 bg-[#2C5530] text-white hover:bg-[#1E3A21] transition-colors" title="Send via email" data-testid={`resend-btn-${index}`}><Mail className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteInvite(inv.id)} className="p-2 text-red-500 hover:bg-red-50 transition-colors" title="Delete" data-testid={`delete-btn-${index}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== INVITE FORM MODAL ==================== */}
      {showInviteForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white w-full max-w-lg" data-testid="invitation-form-modal">
            <div className="border-b border-[#E7E5E4] p-6 flex items-center justify-between">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">
                {createdInvite ? 'Invitation Created!' : 'Invite Client'}
              </h2>
              <button onClick={closeModal} className="text-[#737373] hover:text-[#1A1A1A]"><X className="w-6 h-6" /></button>
            </div>

            {createdInvite ? (
              <div className="p-6 space-y-6">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-[#2C5530] mx-auto mb-4" />
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">Invitation Sent!</h3>
                  <p className="text-[#57534E]">
                    An invitation email has been sent to <strong>{createdInvite.email}</strong> with their unique access code and a link to create their account.
                  </p>
                </div>
                <button onClick={closeModal} className="w-full bg-[#2C5530] text-white px-6 py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors" data-testid="close-invite-modal-btn">Done</button>
              </div>
            ) : (
              <form onSubmit={handleInviteSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Client Email *</label>
                  <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required placeholder="client@example.com" className="w-full" data-testid="invitation-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Client Name</label>
                  <input type="text" value={inviteForm.full_name} onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })} placeholder="John Smith" className="w-full" data-testid="invitation-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Subscription Tier</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setInviteForm({ ...inviteForm, subscription_tier: 'free' })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${inviteForm.subscription_tier === 'free' ? 'border-[#2C5530] bg-[#2C5530]/5' : 'border-[#E7E5E4] hover:border-[#2C5530]'}`}
                      data-testid="tier-free-btn"
                    >
                      <div className="flex items-center gap-2 mb-1"><User className="w-4 h-4 text-[#57534E]" /><span className="font-semibold text-[#1A1A1A]">Free</span></div>
                      <p className="text-xs text-[#737373]">Limited logging (10 sessions/month)</p>
                    </button>
                    <button type="button" onClick={() => setInviteForm({ ...inviteForm, subscription_tier: 'pro_yearly' })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${inviteForm.subscription_tier === 'pro_yearly' ? 'border-[#BC562B] bg-[#BC562B]/5' : 'border-[#E7E5E4] hover:border-[#BC562B]'}`}
                      data-testid="tier-pro-btn"
                    >
                      <div className="flex items-center gap-2 mb-1"><Crown className="w-4 h-4 text-[#BC562B]" /><span className="font-semibold text-[#1A1A1A]">Pro Yearly</span></div>
                      <p className="text-xs text-[#737373]">Full access for 1 year (auto-applied)</p>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Personal Message <span className="text-[#737373] font-normal">(optional)</span></label>
                  <textarea
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                    placeholder="Add a personal note that will be included in the invitation email..."
                    className="w-full resize-none border border-[#E7E5E4] px-4 py-3 text-sm"
                    rows={3}
                    data-testid="invitation-message-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={closeModal} className="flex-1 border border-[#E7E5E4] text-[#57534E] px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#F2F0E9] transition-colors">Cancel</button>
                  <button type="submit" disabled={submitting} className="flex-1 bg-[#BC562B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-invitation-btn">
                    {submitting ? 'Creating...' : 'Create Invitation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;
